import { Column } from 'typeorm';
import type { CampaignKind, CampaignNoLinkedInRule, CampaignNoPhoneRule } from '../../constants/campaign-sequence';
import { Entity } from '../../decorators';
import type { TradeShowOutreachTiming } from '../../constants/trade-shows';
import { BaseEntity } from './base.entity';

export type CampaignObjective =
	| 'cold_acquisition'
	| 'nurture'
	| 'reengagement'
	| 'ar_collection'
	| 'announcement';

/**
 * Who a campaign reaches when contacts are added to it (plan section 1, Q1-2).
 *
 * - `person`: each person once. A person who is a contact at several customers
 *   gets one membership, on the record at their main customer.
 * - `customer`: each person once per customer they are active at, for offers
 *   that are specific to a store or account.
 */
export type CampaignAudience = 'person' | 'customer';

export const CAMPAIGN_AUDIENCES: ReadonlyArray<CampaignAudience> = [
	'person',
	'customer',
];

export type CampaignStatus =
	| 'draft'
	| 'active'
	| 'paused'
	| 'completed'
	| 'archived';

/**
 * A campaign is a Workd-level outreach initiative / offer (e.g. "Workd ID push").
 *
 * In Instantly it fans out to many provider campaigns (each essentially a
 * sequence) — see CampaignProviderCampaignEntity — with a given contact enrolled
 * in exactly one of them. Workd is the system of record for the campaign;
 * Instantly remains the sending + deliverability engine. `provider` plus the
 * external ids on the child tables form the provider-adapter seam so the sending
 * provider can be swapped without remodeling.
 *
 * The record owner is the inherited `ownerUserId`.
 */
@Entity('campaign', {}, { auditable: true })
export class CampaignEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	// App-validated (CampaignObjective). Kept as varchar so the offer taxonomy can
	// grow without an enum-altering migration.
	@Column('varchar', { name: 'objective', length: 50, nullable: true })
	public objective!: CampaignObjective | string | null;

	@Column('varchar', { name: 'status', length: 50, default: () => "'draft'" })
	public status!: CampaignStatus | string;

	@Column('varchar', {
		name: 'provider',
		length: 50,
		default: () => "'instantly'",
	})
	public provider!: string;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('datetime', { name: 'startDate', nullable: true })
	public startDate!: Date | null;

	@Column('datetime', { name: 'endDate', nullable: true })
	public endDate!: Date | null;

	// A database default keeps rows inserted by other writers (Mission Control
	// over the API, any direct insert) on the person default without knowing
	// about the column. App-validated against CAMPAIGN_AUDIENCES.
	@Column('varchar', {
		name: 'audience',
		length: 20,
		default: () => "'person'",
	})
	public audience!: CampaignAudience | string;

	@Column('json', { name: 'settings', nullable: true })
	public settings!: Record<string, unknown> | null;

	// ---------------------------------------------------------------------
	// PD-17613 — campaigns v2: the brief drafts are written from, and the
	// sending setup. All nullable or defaulted, so Mission Control's v1
	// mirror rows and its inserts keep working unchanged.
	// ---------------------------------------------------------------------

	// App-validated (CampaignKind).
	@Column('varchar', { name: 'kind', length: 40, nullable: true })
	public kind!: CampaignKind | string | null;

	@Column('text', { name: 'purpose', nullable: true })
	public purpose!: string | null;

	/** The one thing we ask for. */
	@Column('text', { name: 'ask', nullable: true })
	public ask!: string | null;

	/** Why they'd care. */
	@Column('text', { name: 'valueStatement', nullable: true })
	public valueStatement!: string | null;

	/** What they get for showing up. */
	@Column('text', { name: 'incentive', nullable: true })
	public incentive!: string | null;

	/** Who they are (written from the participants). */
	@Column('text', { name: 'whoTheyAre', nullable: true })
	public whoTheyAre!: string | null;

	@Column('text', { name: 'voice', nullable: true })
	public voice!: string | null;

	@Column('text', { name: 'neverSay', nullable: true })
	public neverSay!: string | null;

	/** Pre-filled from the company-wide default instructions, then the campaign's own. */
	@Column('text', { name: 'writingInstructions', nullable: true })
	public writingInstructions!: string | null;

	// A campaign made for a trade show. The trade_show table arrives with
	// PD-17632, so this is a plain column until then (no relation).
	@Column('int', { name: 'fk_trade_show__id', unsigned: true, nullable: true })
	public fkTradeShowId!: number | null;

	/** PD-17638: before, at or after the show, for the Outreach hub. */
	@Column('varchar', { name: 'tradeShowTiming', length: 10, nullable: true })
	public tradeShowTiming!: TradeShowOutreachTiming | null;

	/**
	 * The Instantly sending setup: sender tags, schedule, daily new people,
	 * mailbox cap, tracking flags. Read by the sequence engine when it creates
	 * the Instantly campaign; anything missing falls back to the tenant's
	 * Instantly defaults (Settings → Integrations → Instantly).
	 */
	@Column('json', { name: 'providerSettings', nullable: true })
	public providerSettings!: CampaignProviderSettingsModel | null;

	// App-validated (CampaignNoPhoneRule); null = skip the call step.
	@Column('varchar', { name: 'noPhoneRule', length: 20, nullable: true })
	public noPhoneRule!: CampaignNoPhoneRule | string | null;

	// App-validated (CampaignNoLinkedInRule); null = skip the LinkedIn step.
	@Column('varchar', { name: 'noLinkedInRule', length: 20, nullable: true })
	public noLinkedInRule!: CampaignNoLinkedInRule | string | null;

	/**
	 * PD-17614 — follow-ups that went out from a different mailbox than the
	 * email before them (from Instantly's sent events). Instantly is expected
	 * to keep a thread on one mailbox; a count above zero flags the campaign.
	 */
	@Column('int', { name: 'mailboxMismatchCount', unsigned: true, default: () => '0' })
	public mailboxMismatchCount!: number;

	@Column('datetime', { name: 'lastMailboxMismatchDate', nullable: true })
	public lastMailboxMismatchDate!: Date | null;
}

/** campaign.providerSettings (PD-17613). Every field optional. */
export interface CampaignProviderSettingsModel {
	/** Instantly account tag ids whose mailboxes send this campaign. Untagged mailboxes are never used. */
	senderTagIds?: Array<string>;
	/** IANA time zone for the sending window. */
	timezone?: string;
	/** Sending window, 'HH:MM' 24-hour. */
	sendFrom?: string;
	sendTo?: string;
	/** Days of the week to send on (0 = Sunday). */
	sendDays?: Array<number>;
	/** New people started per day. */
	dailyNewPeople?: number;
	/** Most emails per mailbox per day for this campaign. */
	mailboxDailyCap?: number;
	openTracking?: boolean;
	linkTracking?: boolean;
}
